import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { HiOutlineRefresh } from "react-icons/hi";
import apiClient from "../../api/client";
import { EXPIRY_BUCKETS, formatCount, formatExpiryDate } from "../../utils/expiry";
import "./index.css";
import LoadError from "../LoadError";
import { getErrorMessage } from "../../utils/network";

/**
 * "Expiry Overview" section shown on every dashboard.
 *
 * One card per provider that is enabled for the logged-in account
 * (decided server-side in dashboard/expiry_summary.php), each with five
 * buttons: expired in the last 7 days, expired yesterday, expires today,
 * expires tomorrow, expires in the next 7 days. Clicking a button opens
 * that provider's customer list already filtered to those customers.
 *
 * Pioneer / DVPL counts need a live call to the OneRADIUS server, so
 * they load separately — a slow provider never blocks the other cards.
 */
const ExpiryOverview = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCause, setErrorCause] = useState(null);
  const [syncingRailtel, setSyncingRailtel] = useState(false);

  const patchProvider = (key, patch) =>
    setProviders((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const loadDeferred = useCallback(async (key, refresh = false) => {
    patchProvider(key, { loading: true, error: null });
    try {
      const { data } = await apiClient.get(
        `/dashboard/expiry_summary.php?provider=${key}${refresh ? "&refresh=1" : ""}`
      );
      if (data.success) {
        patchProvider(key, { ...data.provider, loading: false });
      } else {
        patchProvider(key, { loading: false, error: data.message || "Could not load" });
      }
    } catch (e) {
      patchProvider(key, { loading: false, error: getErrorMessage(e, "Could not reach the server") });
    }
  }, []);

  const loadAll = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await apiClient.get("/dashboard/expiry_summary.php");
        if (!data.success) throw new Error(data.message);
        setToday(data.today);
        setProviders(data.providers.map((p) => ({ ...p, loading: !!p.deferred })));
        data.providers.filter((p) => p.deferred).forEach((p) => loadDeferred(p.key, refresh));
      } catch (e) {
        setError(getErrorMessage(e, "Couldn't load the expiry overview."));
        setErrorCause(e);
      } finally {
        setLoading(false);
      }
    },
    [loadDeferred]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const syncRailtel = async () => {
    setSyncingRailtel(true);
    try {
      const { data } = await apiClient.post("/customer/railtel_sync_expiry.php?limit=25");
      if (data.success) toast.success(data.message);
      else toast.warn(data.message || "RailTel sync incomplete");
      await loadAll();
    } catch (e) {
      toast.error(getErrorMessage(e, "RailTel sync failed"));
    } finally {
      setSyncingRailtel(false);
    }
  };

  const openList = (provider, bucketKey) => {
    const sep = provider.listPath.includes("?") ? "&" : "?";
    navigate(`${provider.listPath}${sep}expiry=${bucketKey}`);
  };

  if (!loading && !error && providers.length === 0) return null;

  return (
    <section className="expiry-overview" aria-labelledby="expiry-overview-title">
      <div className="expiry-overview__head">
        <div>
          <h3 id="expiry-overview-title">Expiry Overview</h3>
          <p className="expiry-overview__sub">
            Renewals due and recently lapsed, per provider{today ? ` · as of ${formatExpiryDate(today)}` : ""}
          </p>
        </div>
        <button
          type="button"
          className="expiry-overview__refresh"
          onClick={() => loadAll(true)}
          disabled={loading}
          aria-label="Refresh expiry overview"
        >
          <HiOutlineRefresh className={loading ? "spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="expiry-legend" aria-hidden="true">
        <span><i className="dot dot--past" /> Already expired</span>
        <span><i className="dot dot--today" /> Expires today</span>
        <span><i className="dot dot--soon" /> Coming up</span>
      </div>

      {error ? (
        <LoadError compact error={errorCause} message={error} onRetry={() => loadAll()} />
      ) : (
        <div className="expiry-grid">
          {(loading ? SKELETON : providers).map((p) => (
            <article key={p.key} className={`expiry-card expiry-card--${p.key}`}>
              <header className="expiry-card__head">
                <span className="expiry-card__name">{p.label}</span>
                {!loading && (p.loading || p.total != null) && (
                  <span className="expiry-card__stats" aria-label={p.loading ? "Loading totals" : `${p.total} total, ${p.active ?? 0} active`}>
                    <span className="expiry-stat">
                      Total <b>{p.loading ? "…" : formatCount(p.total)}</b>
                    </span>
                    <span className="expiry-stat expiry-stat--active">
                      Active <b>{p.loading ? "…" : formatCount(p.active ?? 0)}</b>
                    </span>
                  </span>
                )}
                {p.kind === "broadband" && !p.loading && (
                  <button
                    type="button"
                    className="expiry-card__mini-btn"
                    onClick={() => loadDeferred(p.key, true)}
                    title="Fetch fresh numbers from the provider"
                  >
                    <HiOutlineRefresh />
                  </button>
                )}
              </header>

              {p.error ? (
                <p className="expiry-card__error">
                  {p.error}
                  {p.kind === "broadband" && (
                    <button type="button" onClick={() => loadDeferred(p.key, true)}>Retry</button>
                  )}
                </p>
              ) : (
                <div className="expiry-buttons">
                  {EXPIRY_BUCKETS.map((b) => {
                    const count = p.counts?.[b.key];
                    const isLoading = loading || p.loading || count === undefined;
                    return (
                      <button
                        key={b.key}
                        type="button"
                        className={`expiry-btn expiry-btn--${b.tone} ${!isLoading && !count ? "is-zero" : ""}`}
                        disabled={isLoading}
                        onClick={() => openList(p, b.key)}
                        aria-label={`${p.label}: ${isLoading ? "loading" : count} customers – ${b.short}`}
                        title={b.short}
                      >
                        <span className="expiry-btn__count">
                          {isLoading ? <span className="expiry-skel" /> : formatCount(count)}
                        </span>
                        <span className="expiry-btn__label">{b.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {p.kind === "broadband" && p.note && !p.loading && (
                <footer className="expiry-card__foot"><span>{p.note}</span></footer>
              )}

              {p.kind === "railtel" && !loading && (
                <footer className="expiry-card__foot">
                  <span>
                    {p.note || (p.oldestSync ? `Synced from RailTel · oldest ${p.oldestSync}` : "Based on last RailTel sync")}
                  </span>
                  {!p.error && (
                    <button type="button" onClick={syncRailtel} disabled={syncingRailtel}>
                      {syncingRailtel ? "Syncing…" : "Sync now"}
                    </button>
                  )}
                </footer>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const SKELETON = [
  { key: "s1", label: " ", counts: null },
  { key: "s2", label: " ", counts: null },
];

export default ExpiryOverview;
